import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box, FormControl, FormControlLabel,
  MenuItem, Radio, Select, SelectChangeEvent,
  Typography,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { errorNames } from '../../../utils/constants';
import { convertWeekdaysReverse, weekdays } from '../../../utils/data';
import { borderInput } from '../../formAddress/formAddress.styles';
import { dateForm, dateWrapper, selectCheckBoxWrapper, useStyles } from './dateForm.styles';
import { IProps } from './types';
import { utcToZonedTime } from 'date-fns-tz';

dayjs.extend(utc);

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 'none',
      width: 309,
    },
  },
};

const getNextDayOfWeek = (weekday: string): string => {
  const endOfToday = moment().hour(23).minute(59).second(59);
  const dayOfWeek = moment().day(weekday);
  if (dayOfWeek.isBefore(endOfToday)) {
    dayOfWeek.add(1, 'weeks');
  }
  return dayOfWeek.utc().format();
};

const defaultStartTime = dayjs(new Date()).utc().hour(10).minute(0);
const defaultEndTime = dayjs(new Date()).utc().hour(11).minute(0);

export const DateForm: React.FC<IProps> = (props) => {
  const { register, required, errors, days, setDays, setValue, opportunities } = props;
  const [startTime, setStartTime] = useState<null | Dayjs>(null);
  const [endTime, setEndTime] = useState<null | Dayjs>(null);
  const [date, setDate] = useState<null | Dayjs>(null);
  const classes = useStyles();

  useEffect(() => {
    setStartTime(opportunities?.startDate ? dayjs(opportunities?.startDate).utc() : defaultStartTime);
    setEndTime(opportunities?.endDate ? dayjs(opportunities?.endDate).utc() : defaultEndTime);
    setDate(opportunities?.startDate ? dayjs(opportunities?.startDate).utc() : defaultStartTime);
    setValue('firstOccurrence', opportunities?.startDate || defaultStartTime.format());
    setValue('startTime', opportunities?.startDate || defaultStartTime.format());
    setValue('endTime', opportunities?.endDate || defaultEndTime);
  }, []);

  const isEveryRecurring = useMemo(() => !!days[0]?.toLocaleLowerCase()?.includes('every'), [days]);

  const handleChangeCheckBox = (event: SelectChangeEvent<typeof days>): void => {
    const { target: { value } } = event;
    setDays(
      typeof value === 'string' ? value.split(',') : value,
    );

    if (value === convertWeekdaysReverse.EVERY_DAY[0]) {
      setDate(dayjs());
      setValue('firstOccurrence', dayjs(new Date()).format());
    }
    else if (value !== convertWeekdaysReverse.ONE_DAY[0]) {
      const nextDayOfWeek = getNextDayOfWeek((value as string).split(' ')[1]);
      setDate(dayjs(nextDayOfWeek));
      setValue('firstOccurrence', nextDayOfWeek);
    }
  };

  const handleChangerStartTime = (newValue: Dayjs | null): void => {
    if (newValue?.isValid) {
      setStartTime(newValue);
      setValue('startTime', newValue.format());
    }
  };

  const handleChangerEndTime = (newValue: Dayjs | null): void => {
    if (newValue?.isValid) {
      setEndTime(newValue);
      setValue('endTime', newValue.format());
    }
  };

  const handleChangerDate = (newValue: Dayjs | null): void => {
    if (newValue?.isValid) {
      setDate(newValue);
      setValue('firstOccurrence', newValue.format());
    }
  };

  const validateStartTime = (startTimeCurrent: string | Dayjs | null | undefined) => {
    if (!startTimeCurrent || !endTime || !date) {
      return false;
    }
    const now = dayjs().utc().set('hour', new Date().getHours()).set('minute', new Date().getMinutes());
    const time = (typeof startTimeCurrent === 'string' ? dayjs(startTimeCurrent) : startTimeCurrent).utc();
    const startTimeWithDate = dayjs(date).utc().set('hour', time.hour()).set('minute', time.minute());
    const endTimeWithDate = dayjs(date).utc().set('hour', endTime.hour()).set('minute', endTime.minute());
    return startTimeWithDate.isBefore(endTimeWithDate) && (isEveryRecurring || startTimeWithDate.isAfter(now));
  };

  const validateEndTime = (endTimeCurrent: string | Dayjs | null | undefined) => {
    if (!endTimeCurrent || !startTime || !date) {
      return false;
    }
    const time = (typeof endTimeCurrent === 'string' ? dayjs(endTimeCurrent).utc() : endTimeCurrent).utc(false);
    const endTimeWithDate = dayjs(date).utc().set('hour', time.hour()).set('minute', time.minute());
    const startTimeWithDate = dayjs(date).utc().set('hour', startTime.hour()).set('minute', startTime.minute());
    return startTimeWithDate.isBefore(endTimeWithDate) && (isEveryRecurring || endTimeWithDate.isAfter(dayjs(new Date()).utc()));
  };

  return (
    <div className={classes.dateMainBlock}>
      <Box sx={dateWrapper}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={dateForm}>
            <DesktopDatePicker
              {...register('firstOccurrence', { required })}
              disabled={days[0] !== weekdays[0]}
              label="Date*"
              format="DD-MM-YYYY"
              value={date}
              onChange={handleChangerDate}
              sx={borderInput}
              slotProps={{
                textField: {
                  error: errors?.firstOccurrence?.type === 'required',
                  helperText: errors?.firstOccurrence?.type === 'required' ? errorNames.dateRequired : undefined,
                },
              }}
            />
            <TimePicker
              {...register('startTime', {
                required,
                validate: validateStartTime,
              })}
              value={startTime?.utc()}
              label="Time start*"
              views={['hours', 'minutes']}
              format="h:mm A"
              onChange={handleChangerStartTime}
              sx={borderInput}
              slotProps={{
                textField: {
                  error: errors?.startTime?.type === 'required' || errors?.startTime?.type === 'validate',
                  helperText: errors?.startTime?.type === 'required' ? errorNames.startTimeRequired : (errors?.startTime?.type === 'validate' ? errorNames.startTimeValidate : ''),
                },
              }}
            />
            <TimePicker
              {...register('endTime', {
                required,
                validate: validateEndTime,
              })}
              label="Time end*"
              value={endTime?.utc()}
              views={['hours', 'minutes']}
              onChange={handleChangerEndTime}
              sx={borderInput}
              slotProps={{
                textField: {
                  error: errors?.endTime?.type === 'required' || errors?.endTime?.type === 'validate',
                  helperText: errors?.endTime?.type === 'required' ? errorNames.endTimeRequired : (errors?.endTime?.type === 'validate' ? errorNames.endTimeValidate : ''),
                },
              }}
            />
          </Box>
        </LocalizationProvider>
        <Box sx={selectCheckBoxWrapper} >
          <Typography color={'rgba(0, 0, 0, 0.5)'}>Recurring</Typography>
          <FormControl className={classes.selectBlock}>
            <Select
              className={classes.select}
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              value={days}
              onChange={handleChangeCheckBox}
              renderValue={(selected) => selected.join(', ')}
              MenuProps={MenuProps}
              IconComponent={KeyboardArrowDownIcon}
              // disabled={!!opportunities}
            >
              {weekdays.map((day) => (
                <MenuItem className={classes.dropDownItem} key={day} value={day}>
                  <FormControlLabel
                    value={day}
                    label={day}
                    control={<Radio checked={days && days.indexOf(day) > -1} />}
                    className={`${classes.checkbox} ${days && days.indexOf(day) > -1 && classes.checkedRadio}`}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </div>
  );
};
